import React, { useState, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  createBlogService,
  getBlogBySlug,
  editBlog,
} from "services/BlogService";
import { useParams } from "react-router-dom";
import NotificationSystem from "react-notification-system";

export const CreateEditBlog = () => {
  const notification = useRef()
  const { slug: urlSlug } = useParams();
  const [title, setTitle] = useState("");
  const [metadata, setMetadata] = useState("");
  const [slug, setSlug] = useState("");
  const [image, setImage] = useState(null);
  const [content, setContent] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (urlSlug) {
      setLoading(true);
      getBlogBySlug(urlSlug)
        .then((res) => {
          const blog = res.data.data;
          setTitle(blog.title || "");
          setMetadata(blog.metadata || "");
          setSlug(blog.slug || "");
          setContent(blog.content || "");
          setPreview(blog.image || null);
          setImage(null); // Only set image if user uploads new one
        })
        .finally(() => setLoading(false));
    }
  }, [urlSlug]);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setImage(null);
      setPreview(null);
      return;
    }

    // Compress image using canvas
    const compressImage = (file, quality = 0.7, maxWidth = 1200) =>
      new Promise((resolve) => {
        const img = new window.Image();
        const reader = new FileReader();
        reader.onload = (ev) => {
          img.onload = () => {
            const canvas = document.createElement("canvas");
            let width = img.width;
            let height = img.height;
            if (width > maxWidth) {
              height = (maxWidth / width) * height;
              width = maxWidth;
            }
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob(
              (blob) => {
                resolve(blob);
              },
              "image/jpeg",
              quality
            );
          };
          img.src = ev.target.result;
        };
        reader.readAsDataURL(file);
      });

    const compressedBlob = await compressImage(file);
    setImage(compressedBlob);

    // For preview
    setPreview(URL.createObjectURL(compressedBlob));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = {
      title,
      metadata,
      slug,
      content,
      image,
    };
    try {
      if (urlSlug) {
        await editBlog(urlSlug, formData);
        notification.current.addNotification({
          message: "Blog updated successfully!",
          level: "success",
          position: "tc",
          autoDismiss: "5",
          dismissible: "both",
        });
      } else {
        await createBlogService(formData);
        notification.current.addNotification({
          message: "Blog added successfully!",
          level: "success",
          position: "tc",
          autoDismiss: "5",
          dismissible: "both",
        });
      }
      // Optionally reset form fields here
    } catch (error) {
      if (error.response && error.response.data && error.response.data.data) {
        notification.current.addNotification({
          message: error.response.data.data,
          level: "error",
          position: "tc",
          autoDismiss: "5",
          dismissible: "both",
        });
      } else if (error.message) {
        notification.current.addNotification({
          message: `Error: ${error.message}`,
          level: "error",
          position: "tc",
          autoDismiss: "5",
          dismissible: "both",
        });
      } else {
        notification.current.addNotification({
          message: "An unknown error occurred.",
          level: "error",
          position: "tc",
          autoDismiss: "5",
          dismissible: "both",
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="w-90 mx-auto mt-5-vh text-center text-muted">
        Loading...
      </div>
    );
  }

  return (
    <div className="w-90 mx-auto mt-5-vh mb-5">
      <NotificationSystem ref={notification} />
      <h2 className="blue mb-4">Create Blog Post</h2>
      <form
        onSubmit={handleSubmit}
        className="bg-white p-4 shadow"
        style={{ maxWidth: 800 }}
      >
        <div className="mb-3">
          <label className="form-label">Title</label>
          <input
            type="text"
            className="form-control"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Metadata</label>
          <input
            type="text"
            className="form-control"
            value={metadata}
            onChange={(e) => setMetadata(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Slug</label>
          <input
            type="text"
            className="form-control"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Image</label>
          <input
            type="file"
            className="form-control"
            accept="image/*"
            onChange={handleImageChange}
          />
          {preview && (
            <img
              src={preview}
              alt="Preview"
              style={{ width: 200, marginTop: 10, borderRadius: 8 }}
            />
          )}
        </div>
        <div className="mb-3">
          <label className="form-label">Content</label>
          <ReactQuill
            theme="snow"
            value={content}
            onChange={setContent}
            style={{ height: 200, marginBottom: 40 }}
          />
        </div>
        <button type="submit" className="btn btn-primary">
          Submit
        </button>
      </form>
    </div>
  );
};

export default CreateEditBlog;
